import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ArrowDownToLine, ArrowUpFromLine, PlusCircle, History, FileText } from 'lucide-react';

const inr = (n) => `₹${(Math.round((n || 0) * 100) / 100).toLocaleString('en-IN')}`;

const TYPE_META = {
  create: { label: 'Created', icon: PlusCircle, color: 'text-[#5B6675]', bg: 'bg-[#EFF2F7]' },
  add: { label: 'Restocked', icon: ArrowDownToLine, color: 'text-[#0B4F3F]', bg: 'bg-[#0B4F3F]/10' },
  sell: { label: 'Sold', icon: ArrowUpFromLine, color: 'text-[#0B4F3F]', bg: 'bg-[#0B4F3F]/10' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString([], {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function InventoryHistorySheet({ inventoryId, onClose }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!inventoryId) return;
    try {
      const { data } = await api.get(`/admin/inventory/${inventoryId}/transactions`, {
        params: { limit: 200 },
      });
      setData(data);
    } catch {} finally {
      setLoading(false);
    }
  }, [api, inventoryId]);

  useEffect(() => { load(); }, [load]);

  const item = data?.item;
  const txns = data?.transactions || [];

  return (
    <Dialog open={!!inventoryId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md rounded-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="history-sheet"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-[#0B4F3F]" /> Transaction history
          </DialogTitle>
          <DialogDescription>
            {item ? (
              <>
                {item.variant_name} · {item.units} units left · {inr(item.total_profit || 0)} net
              </>
            ) : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6 pb-2">
          {loading ? (
            <div className="space-y-2">
              {[1,2,3].map(i => (
                <div key={i} className="h-14 bg-[#EFF2F7] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : txns.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#5B6675]">
              <FileText className="w-8 h-8 mx-auto text-[#5B6675]/30 mb-2" />
              No transactions yet
            </div>
          ) : (
            <ul className="divide-y divide-[#E4E8EF]" data-testid="history-list">
              {txns.map(t => {
                const meta = TYPE_META[t.type] || TYPE_META.add;
                const Icon = meta.icon;
                const showProfit = t.type === 'sell' && t.profit !== undefined;
                const profitColor = (t.profit || 0) > 0
                  ? 'text-[#15803D]'
                  : (t.profit || 0) < 0
                    ? 'text-[#B91C1C]'
                    : 'text-[#5B6675]';
                return (
                  <li key={t.id} className="py-2.5 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                      <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-[#0B1620]">
                          {meta.label} {t.units} units
                        </p>
                        <p className="text-[10px] text-[#5B6675]">{fmtDate(t.at)}</p>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-[#5B6675] mt-0.5">
                        {t.type === 'sell' ? (
                          <>
                            <span>@{inr(t.sale_price_per_unit)}/unit</span>
                            <span>= {inr(t.amount)} revenue</span>
                          </>
                        ) : (
                          <>
                            <span>@{inr(t.purchase_cost_per_unit)}/unit</span>
                            <span>= {inr(t.amount)} spent</span>
                          </>
                        )}
                        <span className="ml-auto">balance: {t.balance_after}</span>
                      </div>
                      {showProfit && (
                        <p className={`text-[11px] font-semibold mt-0.5 ${profitColor}`}>
                          {(t.profit || 0) >= 0 ? 'Profit' : 'Loss'}: {inr(Math.abs(t.profit || 0))}
                          <span className="text-[10px] text-[#5B6675]/70 font-normal ml-1">
                            (cost basis {inr(t.cost_basis_per_unit)}/unit)
                          </span>
                        </p>
                      )}
                      {t.notes && (
                        <p className="text-[11px] text-[#5B6675] italic mt-0.5 truncate">"{t.notes}"</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
